/**
 * The state of one principal's result within a bulk Authgard operation:
 *   - SUCCESS: the backend returned a result and it succeeded (`value` carries the payload).
 *   - FAILURE: the backend explicitly failed this principal (`errorCode` says why).
 *   - UNKNOWN: the backend returned no result for a principal we requested — the outcome is
 *              undetermined (for a write, it may have partially applied). Callers treat this as an
 *              error, not a success.
 */
export enum AuthgardResultStatus {
    SUCCESS = 'success',
    FAILURE = 'failure',
    UNKNOWN = 'unknown',
}

/**
 * One principal's result, keyed by principalArn in the map the providers return. Every requested
 * principalArn is present in the map, so `map.get(requestedArn)` is always defined. A total request
 * failure is thrown by the provider, not represented here.
 */
export type AuthgardItemResult<T> =
    | { status: AuthgardResultStatus.SUCCESS; value: T }
    | { status: AuthgardResultStatus.FAILURE; errorCode: string; errorMessage: string }
    | { status: AuthgardResultStatus.UNKNOWN };

export interface PartitionedResults<T> {
    successes: Array<{ principalArn: string; value: T }>;
    failures: Array<{ principalArn: string; errorCode: string; errorMessage: string }>;
    /** principalArns whose outcome is unknown (no result returned). */
    unknown: string[];
}

/**
 * Splits a results map into successes / failures / unknown for callers (e.g. a bulk view) that need
 * to report "N succeeded, M failed, K unknown". Derived from the map so the map stays the single
 * source of truth.
 */
export const partitionAuthgardResults = <T>(
    results: Map<string, AuthgardItemResult<T>>,
): PartitionedResults<T> => {
    const partitioned: PartitionedResults<T> = { successes: [], failures: [], unknown: [] };
    for (const [principalArn, result] of results) {
        if (result.status === AuthgardResultStatus.SUCCESS) {
            partitioned.successes.push({ principalArn, value: result.value });
        } else if (result.status === AuthgardResultStatus.FAILURE) {
            partitioned.failures.push({ principalArn, errorCode: result.errorCode, errorMessage: result.errorMessage });
        } else {
            partitioned.unknown.push(principalArn);
        }
    }
    return partitioned;
};
